// src/pages/help/Tutorial.jsx
// V2 PREMIUM — Tutorial interativo ultra-moderno
// ============================================================
// CORRECOES v2:
// [FIX-1]  Todos os artefatos http://BRAND.x removidos — JS puro
// [FIX-2]  avancar/voltar/irParaModulo envolvidos em useCallback
// [FIX-3]  auto-concluir no useEffect sem concluirPasso nas deps (stale ref)
// [FIX-4]  todosCompletos usado no indicador de progresso
// [FIX-5]  concluirPasso estabilizado com useRef para passosConcluidos/modulosConcluidos
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Target, CheckCircle2, ArrowRight,
  ArrowLeft, Building, Users, DollarSign, CheckSquare,
  Zap, Star, Trophy, PartyPopper,
} from "lucide-react";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { useAuth } from "../../contexts/useAuth";
import { useNavigate } from "react-router-dom";

// ============================================================
// 🎨 BRANDING
// ============================================================
const BRAND = {
  primary:      "#1a56db",
  primaryLight: "#3b82f6",
  primaryDark:  "#1e3a5f",
  gold:         "#c8a84e",
  goldLight:    "#e2c97e",
  goldDark:     "#8b6914",
  bg:           "#f0f4ff",
  success:      "#10b981",
};

// Keyframes injetados uma vez
const TUTORIAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

  .tut-root { font-family: 'DM Sans', sans-serif; }
  .tut-heading { font-family: 'Sora', sans-serif; }

  @keyframes goldShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes floatBadge {
    0%, 100% { transform: translateY(0) rotate(-2deg); }
    50%       { transform: translateY(-6px) rotate(2deg); }
  }
  @keyframes pulseRing {
    0%   { transform: scale(1);    opacity: 0.6; }
    100% { transform: scale(1.55); opacity: 0;   }
  }
  @keyframes confettiFall {
    0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
    100% { transform: translateY(80px)  rotate(360deg); opacity: 0; }
  }
  @keyframes checkPop {
    0%   { transform: scale(0) rotate(-30deg); }
    60%  { transform: scale(1.25) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg);   }
  }

  .gold-shimmer {
    background: linear-gradient(90deg, #c8a84e 0%, #f0d98a 40%, #c8a84e 60%, #8b6914 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: goldShimmer 3s linear infinite;
  }
  .float-badge { animation: floatBadge 3s ease-in-out infinite; }
  .pulse-ring {
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid #c8a84e;
    animation: pulseRing 1.8s ease-out infinite;
  }
  .check-pop { animation: checkPop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) both; }
  .scrollbar-hide::-webkit-scrollbar { display: none; }
  .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

  .glass-card {
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }
  .mesh-bg {
    background:
      radial-gradient(ellipse 80% 60% at 20% 10%, rgba(26,86,219,0.10) 0%, transparent 60%),
      radial-gradient(ellipse 60% 50% at 80% 80%, rgba(200,168,78,0.12) 0%, transparent 60%),
      #f0f4ff;
  }
`;

// ============================================================
// 📚 MÓDULOS DO TUTORIAL
// ============================================================
const MODULOS = [
  {
    id:       "boas-vindas",
    icon:     PartyPopper,
    title:    "Boas-Vindas",
    emoji:    "🎉",
    duration: "2 min",
    color:    BRAND.gold,
    passos: [
      {
        title:   "🎯 Seu Primeiro Acesso",
        content: "Você acaba de entrar no sistema mais completo para imobiliárias do Brasil. Em menos de 15 minutos vai dominar todas as ferramentas para vender mais imóveis.",
        tip:     "💡 Dica: Adicione esta página aos favoritos (Ctrl+D) para acessar rapidamente.",
        action:  null,
      },
      {
        title:   "📱 Instale o App no Celular",
        content: "O Ilha Leads é um PWA — funciona como um aplicativo nativo, sem precisar de loja!",
        tip:     "1. Abra o menu do navegador (⋮)\n2. Toque em 'Adicionar à tela inicial'\n3. Pronto! Ícone na tela como qualquer app",
        action:  null,
      },
      {
        title:   "🎨 Sua Marca, Suas Cores",
        content: "O sistema é white-label: personalize com o logo, nome e cores da sua imobiliária.",
        tip:     "Acesse Configurações > Empresa para personalizar tudo.",
        action:  { label: "Ir para Configurações", path: "/settings" },
      },
    ],
  },
  {
    id:       "imoveis",
    icon:     Building,
    title:    "Cadastrar Imóveis",
    emoji:    "🏠",
    duration: "3 min",
    color:    BRAND.primary,
    passos: [
      {
        title:   "➕ Criar seu Primeiro Imóvel",
        content: "Clique em 'Novo Imóvel' e preencha as informações básicas. Não se preocupe — você pode editar depois.",
        tip:     "💡 Imóveis com mais campos preenchidos aparecem primeiro nas buscas.",
        action:  { label: "Cadastrar Imóvel", path: "/admin/imoveis/novo" },
      },
      {
        title:   "📸 Fotos que Vendem",
        content: "Adicione fotos de qualidade! Arraste várias imagens de uma vez ou clique para selecionar. A primeira foto é a capa do anúncio.",
        tip:     "💡 Fotos em boa luz e ângulos amplos convertem até 3x mais.",
        action:  null,
      },
      {
        title:   "🌐 Seu Imóvel na Internet",
        content: "Todo imóvel cadastrado aparece automaticamente na sua vitrine pública. Clientes podem ver, filtrar e compartilhar via WhatsApp.",
        tip:     "Acesse /imoveis para ver sua vitrine pública.",
        action:  { label: "Ver Vitrine", path: "/imoveis" },
      },
    ],
  },
  {
    id:       "clientes",
    icon:     Users,
    title:    "Gerenciar Clientes",
    emoji:    "👥",
    duration: "3 min",
    color:    "#8b5cf6",
    passos: [
      {
        title:   "📋 Cadastro de Clientes",
        content: "Cada cliente que entra em contato deve ser cadastrado. Assim você não perde nenhuma oportunidade.",
        tip:     "💡 Vincule o cliente aos imóveis de interesse. O sistema gera recomendações inteligentes!",
        action:  { label: "Cadastrar Cliente", path: "/clientes" },
      },
      {
        title:   "🔄 Pipeline de Vendas",
        content: "Arraste os cards pelo funil: Lead → Contato → Visita → Proposta → Fechamento. Visualize em que etapa cada cliente está.",
        tip:     "💡 Clientes parados há mais de 7 dias aparecem em amarelo. Não deixe esfriar!",
        action:  null,
      },
      {
        title:   "💬 WhatsApp Direto do Sistema",
        content: "Envie mensagens sem sair do sistema. O histórico fica salvo para você consultar depois.",
        tip:     "💡 Use os templates prontos para agilizar o atendimento.",
        action:  { label: "Ver Mensagens", path: "/mensagens" },
      },
    ],
  },
  {
    id:       "financeiro",
    icon:     DollarSign,
    title:    "Controle Financeiro",
    emoji:    "💰",
    duration: "3 min",
    color:    "#10b981",
    passos: [
      {
        title:   "📝 Registre Tudo",
        content: "Lance receitas (vendas, aluguéis, comissões) e despesas (custos, marketing). O sistema calcula tudo automaticamente.",
        tip:     "💡 Categorize cada lançamento para relatórios mais precisos.",
        action:  { label: "Ir para Financeiro", path: "/financeiro" },
      },
      {
        title:   "📊 Gráficos e Análises",
        content: "Veja sua evolução mês a mês. Compare receitas vs despesas. Acompanhe o crescimento em tempo real.",
        tip:     "💡 Use os filtros de mês e tipo para análises específicas.",
        action:  null,
      },
      {
        title:   "📄 Relatórios Profissionais",
        content: "Exporte relatórios completos em PDF. Perfeito para prestar contas ou apresentar resultados.",
        tip:     "💡 Compartilhe o relatório direto via WhatsApp!",
        action:  { label: "Gerar Relatório", path: "/relatorios" },
      },
    ],
  },
  {
    id:       "tarefas",
    icon:     CheckSquare,
    title:    "Organizar Tarefas",
    emoji:    "✅",
    duration: "2 min",
    color:    "#f59e0b",
    passos: [
      {
        title:   "📝 Crie Tarefas para Não Esquecer",
        content: "Visitas, vistorias, ligações, follow-ups — transforme tudo em tarefa com data e prioridade.",
        tip:     "💡 Vincule a tarefa a um cliente ou imóvel para ter todo o contexto.",
        action:  { label: "Criar Tarefa", path: "/tarefas/nova" },
      },
      {
        title:   "📅 Calendário Integrado",
        content: "Todas as tarefas aparecem no calendário. Visualize por mês, semana ou dia. Nunca mais perca um compromisso.",
        tip:     "💡 Tarefas vencidas aparecem em vermelho no dashboard.",
        action:  { label: "Ver Calendário", path: "/calendario" },
      },
    ],
  },
  {
    id:       "final",
    icon:     Trophy,
    title:    "Você está pronto!",
    emoji:    "🏆",
    duration: "1 min",
    color:    BRAND.gold,
    passos: [
      {
        title:   "🏆 Certificado de Conclusão",
        content: "Parabéns! Você completou o tutorial e está pronto para usar o Ilha Leads como um profissional.",
        tip:     "🎯 Meta: cadastre 10 imóveis esta semana e veja a mágica acontecer.",
        action:  null,
      },
      {
        title:   "🚀 O que fazer agora?",
        content: "1. Personalize sua marca em Configurações\n2. Cadastre seus primeiros 5 imóveis\n3. Compartilhe sua vitrine no WhatsApp\n4. Acompanhe o dashboard diariamente",
        tip:     "💡 Quanto mais você usa, mais inteligente o sistema fica!",
        action:  null,
      },
      {
        title:   "⭐ Suporte sempre disponível",
        content: "Se tiver qualquer dúvida, nossa equipe está pronta para ajudar. Acesse Ajuda > Suporte a qualquer momento.",
        tip:     "💡 Documentação completa em Ajuda > Documentação.",
        action:  null,
      },
    ],
  },
];

// ============================================================
// PARTÍCULAS DE CONFETTI (celebração)
// ============================================================
function Confetti() {
  const particles = Array.from({ length: 18 }, (_, i) => ({
    id:    i,
    color: i % 3 === 0 ? BRAND.gold : i % 3 === 1 ? BRAND.primary : BRAND.primaryLight,
    left:  `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.8}s`,
    size:  Math.random() * 6 + 4,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          style={{
            position:         "absolute",
            top:              "-10px",
            left:             p.left,
            width:            `${p.size}px`,
            height:           `${p.size}px`,
            borderRadius:     p.id % 2 === 0 ? "50%" : "2px",
            backgroundColor:  p.color,
            animation:        `confettiFall 1.4s ${p.delay} ease-out both`,
          }}
        />
      ))}
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function Tutorial() {
  const isMobile = useIsMobile();
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [moduloAtual,       setModuloAtual]       = useState(0);
  const [passoAtual,        setPassoAtual]         = useState(0);
  const [modulosConcluidos, setModulosConcluidos]  = useState(new Set());
  const [passosConcluidos,  setPassosConcluidos]   = useState(new Set());
  const [mostrarCelebracao, setMostrarCelebracao]  = useState(false);
  const [celebrou,          setCelebrou]           = useState(false);

  // FIX-5: refs para evitar stale closures nos callbacks
  const passosRef  = useRef(passosConcluidos);
  const modulosRef = useRef(modulosConcluidos);
  useEffect(() => { passosRef.current  = passosConcluidos; }, [passosConcluidos]);
  useEffect(() => { modulosRef.current = modulosConcluidos; }, [modulosConcluidos]);

  const modulo = MODULOS[moduloAtual];
  const passo  = modulo?.passos[passoAtual];

  const totalPassos  = MODULOS.reduce((s, m) => s + m.passos.length, 0);
  const passoGlobal  = MODULOS.slice(0, moduloAtual).reduce((s, m) => s + m.passos.length, 0) + passoAtual + 1;
  const progresso    = Math.round((passoGlobal / totalPassos) * 100);
  const todosCompletos = modulosConcluidos.size === MODULOS.length;

  // Injeta estilos uma vez
  useEffect(() => {
    if (typeof document === "undefined") return;
    const id = "tutorial-v2-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id    = id;
      tag.textContent = TUTORIAL_STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  // FIX-3: auto-concluir com refs estáveis — sem concluirPasso nas deps
  useEffect(() => {
    const key      = `${moduloAtual}-${passoAtual}`;
    const prevPassos  = passosRef.current;
    const prevModulos = modulosRef.current;

    if (prevPassos.has(key)) return; // já concluído

    const newPassos = new Set(prevPassos);
    newPassos.add(key);
    setPassosConcluidos(newPassos);

    const todosPassosModulo = modulo.passos.every((_, i) =>
      newPassos.has(`${moduloAtual}-${i}`)
    );
    if (todosPassosModulo) {
      const newModulos = new Set(prevModulos);
      newModulos.add(moduloAtual);
      setModulosConcluidos(newModulos);
    }

    // Celebração só na primeira vez que terminar tudo
    if (
      moduloAtual === MODULOS.length - 1 &&
      passoAtual  === modulo.passos.length - 1 &&
      !celebrou
    ) {
      setCelebrou(true);
      setTimeout(() => setMostrarCelebracao(true), 400);
    }
  }, [moduloAtual, passoAtual]); // eslint-disable-line react-hooks/exhaustive-deps

  // FIX-2: callbacks estáveis
  const avancar = useCallback(() => {
    if (passoAtual < modulo.passos.length - 1) {
      setPassoAtual((p) => p + 1);
    } else if (moduloAtual < MODULOS.length - 1) {
      setModuloAtual((m) => m + 1);
      setPassoAtual(0);
    }
  }, [passoAtual, moduloAtual, modulo.passos.length]);

  const voltar = useCallback(() => {
    if (passoAtual > 0) {
      setPassoAtual((p) => p - 1);
    } else if (moduloAtual > 0) {
      const prevModulo = MODULOS[moduloAtual - 1];
      setModuloAtual((m) => m - 1);
      setPassoAtual(prevModulo.passos.length - 1);
    }
  }, [passoAtual, moduloAtual]);

  const irParaModulo = useCallback((index) => {
    setModuloAtual(index);
    setPassoAtual(0);
  }, []);

  const isUltimoPasso = moduloAtual === MODULOS.length - 1 && passoAtual === modulo.passos.length - 1;
  const isPrimeiroPasso = moduloAtual === 0 && passoAtual === 0;

  return (
    <div className="tut-root min-h-screen mesh-bg">

      {/* ═══════════ HEADER ═══════════ */}
      <header
        className="sticky top-0 z-50 border-b"
        style={{
          background:   `linear-gradient(135deg, ${BRAND.primaryDark} 0%, ${BRAND.primary} 100%)`,
          borderColor:  `${BRAND.gold}30`,
          boxShadow:    "0 4px 24px rgba(26,86,219,0.25)",
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2.5">
              <div
                className="p-1.5 rounded-lg"
                style={{ background: `${BRAND.gold}25` }}
              >
                <GraduationCap size={18} style={{ color: BRAND.gold }} />
              </div>
              <h1 className="tut-heading font-bold text-white text-base md:text-lg tracking-tight">
                Tutorial Interativo
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Badge de progresso */}
              <div
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  background:   `${BRAND.gold}20`,
                  color:        BRAND.goldLight,
                  border:       `1px solid ${BRAND.gold}30`,
                }}
              >
                <Zap size={11} />
                {passoGlobal}/{totalPassos}
              </div>
              <span
                className="tut-heading text-base font-bold"
                style={{ color: BRAND.goldLight }}
              >
                {progresso}%
              </span>
            </div>
          </div>

          {/* Barra de progresso premium */}
          <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.12)" }}>
            <motion.div
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldLight}, ${BRAND.gold})`,
                backgroundSize: "200% auto",
              }}
              animate={{ width: `${progresso}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>
      </header>

      {/* ═══════════ CONTEÚDO ═══════════ */}
      <div className="max-w-4xl mx-auto px-4 pb-12 pt-6 md:px-8">

        {/* ── Navegação dos Módulos ── */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-7 scrollbar-hide">
          {MODULOS.map((mod, i) => {
            const Icon      = mod.icon;
            const concluido = modulosConcluidos.has(i);
            const ativo     = i === moduloAtual;

            return (
              <motion.button
                key={mod.id}
                onClick={() => irParaModulo(i)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 border"
                style={
                  ativo
                    ? {
                        background:  `linear-gradient(135deg, ${mod.color}, ${mod.color}cc)`,
                        color:       "#fff",
                        borderColor: "transparent",
                        boxShadow:   `0 4px 14px ${mod.color}50`,
                      }
                    : concluido
                    ? {
                        background:   "#f0fdf4",
                        color:        "#059669",
                        borderColor:  "#a7f3d0",
                      }
                    : {
                        background:   "rgba(255,255,255,0.8)",
                        color:        "#64748b",
                        borderColor:  "#e2e8f0",
                      }
                }
              >
                {concluido && !ativo ? (
                  <CheckCircle2 size={13} className="check-pop" />
                ) : (
                  <Icon size={13} />
                )}
                <span className="hidden sm:inline">{mod.title}</span>
                <span className="sm:hidden text-base">{mod.emoji}</span>
              </motion.button>
            );
          })}
        </div>

        {/* ── Card do Passo Atual ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${moduloAtual}-${passoAtual}`}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0,  scale: 1    }}
            exit={{    opacity: 0, y: -16, scale: 0.98 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="glass-card rounded-2xl overflow-hidden"
            style={{
              border:    `1.5px solid ${modulo.color}25`,
              boxShadow: `0 8px 40px ${modulo.color}15, 0 2px 8px rgba(0,0,0,0.06)`,
            }}
          >
            {/* Faixa colorida + número do passo */}
            <div
              className="relative px-6 pt-6 pb-5 overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${modulo.color}12 0%, ${modulo.color}04 100%)`,
                borderBottom: `1px solid ${modulo.color}15`,
              }}
            >
              {/* Detalhe geométrico */}
              <div
                className="absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-[0.06] pointer-events-none"
                style={{ background: modulo.color }}
              />

              <div className="flex items-start justify-between gap-4 relative">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                      style={{
                        color:           modulo.color,
                        backgroundColor: `${modulo.color}15`,
                        border:          `1px solid ${modulo.color}25`,
                      }}
                    >
                      Módulo {moduloAtual + 1} · {modulo.duration}
                    </span>
                  </div>
                  <h2
                    className="tut-heading text-lg md:text-xl font-bold leading-snug"
                    style={{ color: BRAND.primaryDark }}
                  >
                    {passo.title}
                  </h2>
                </div>

                {/* Emoji flutuante */}
                <div className="float-badge text-3xl flex-shrink-0 select-none">
                  {modulo.emoji}
                </div>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="px-6 py-5">
              {/* Texto principal */}
              <div className="space-y-1 mb-5">
                {passo.content.split("\n").map((line, i) => (
                  <p
                    key={i}
                    className="text-sm md:text-base leading-relaxed"
                    style={{ color: "#334155" }}
                  >
                    {line}
                  </p>
                ))}
              </div>

              {/* Tip dourado */}
              {passo.tip && (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0  }}
                  transition={{ delay: 0.15 }}
                  className="rounded-xl p-4 mb-5"
                  style={{
                    background:   `linear-gradient(135deg, ${BRAND.gold}10, ${BRAND.gold}05)`,
                    border:       `1px solid ${BRAND.gold}30`,
                  }}
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className="p-1.5 rounded-lg flex-shrink-0 mt-0.5"
                      style={{ background: `${BRAND.gold}20` }}
                    >
                      <Zap size={13} style={{ color: BRAND.gold }} />
                    </div>
                    <div className="space-y-1">
                      {passo.tip.split("\n").map((line, i) => (
                        <p
                          key={i}
                          className="text-xs md:text-sm leading-relaxed"
                          style={{ color: BRAND.goldDark }}
                        >
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Botão de ação */}
              {passo.action && (
                <motion.button
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  whileHover={{ y: -2, boxShadow: `0 8px 24px ${modulo.color}40` }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate(passo.action.path)}
                  className="w-full py-3 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${modulo.color}, ${modulo.color}cc)`,
                    boxShadow:  `0 4px 16px ${modulo.color}35`,
                  }}
                >
                  {passo.action.label}
                  <ArrowRight size={16} />
                </motion.button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* ── Indicadores de passo (dots) ── */}
        <div className="flex items-center justify-center gap-2 mt-5 mb-1">
          {modulo.passos.map((_, i) => {
            const concluido = passosConcluidos.has(`${moduloAtual}-${i}`);
            const ativo     = i === passoAtual;
            return (
              <motion.button
                key={i}
                onClick={() => setPassoAtual(i)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className="rounded-full transition-all"
                style={{
                  width:      ativo ? "22px" : "8px",
                  height:     "8px",
                  background: ativo
                    ? modulo.color
                    : concluido
                    ? `${modulo.color}55`
                    : `${modulo.color}20`,
                }}
              />
            );
          })}
        </div>

        {/* ── Navegação prev/next ── */}
        <div className="flex justify-between items-center mt-5">
          <motion.button
            whileHover={{ x: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={voltar}
            disabled={isPrimeiroPasso}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-30 border"
            style={{
              color:       BRAND.primary,
              borderColor: `${BRAND.primary}25`,
              background:  "rgba(255,255,255,0.8)",
            }}
          >
            <ArrowLeft size={15} />
            Voltar
          </motion.button>

          {/* Indicador central */}
          <span
            className="text-xs font-medium"
            style={{ color: `${BRAND.primaryDark}60` }}
          >
            {passoAtual + 1} / {modulo.passos.length}
          </span>

          {isUltimoPasso ? (
            <motion.button
              whileHover={{ y: -2, boxShadow: `0 8px 24px ${BRAND.gold}50` }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setMostrarCelebracao(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white"
              style={{
                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})`,
                boxShadow:  `0 4px 16px ${BRAND.gold}40`,
              }}
            >
              <Trophy size={15} />
              Finalizar
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ x: 2, boxShadow: `0 8px 24px ${BRAND.primary}40` }}
              whileTap={{ scale: 0.95 }}
              onClick={avancar}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
              style={{
                background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`,
                boxShadow:  `0 4px 16px ${BRAND.primary}30`,
              }}
            >
              Próximo
              <ArrowRight size={15} />
            </motion.button>
          )}
        </div>

        {/* Barra de módulos completos */}
        {todosCompletos && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0  }}
            className="mt-6 p-4 rounded-2xl flex items-center gap-3"
            style={{
              background: `linear-gradient(135deg, ${BRAND.gold}12, ${BRAND.gold}06)`,
              border:     `1px solid ${BRAND.gold}30`,
            }}
          >
            <Star size={18} fill={BRAND.gold} style={{ color: BRAND.gold, flexShrink: 0 }} />
            <p className="text-sm font-semibold" style={{ color: BRAND.goldDark }}>
              Tutorial 100% concluído! Você é um especialista Ilha Leads. 🎉
            </p>
          </motion.div>
        )}
      </div>

      {/* ═══════════ MODAL DE CELEBRAÇÃO ═══════════ */}
      <AnimatePresence>
        {mostrarCelebracao && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,42,0.75)", backdropFilter: "blur(8px)" }}
            onClick={() => setMostrarCelebracao(false)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 40, opacity: 0 }}
              animate={{ scale: 1,   y: 0,  opacity: 1 }}
              exit={{    scale: 0.8, y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 18, stiffness: 140 }}
              className="relative bg-card rounded-3xl p-8 max-w-sm w-full text-center overflow-hidden"
              style={{
                border:    `2px solid ${BRAND.gold}40`,
                boxShadow: `0 32px 80px rgba(0,0,0,0.35), 0 0 0 1px ${BRAND.gold}20`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Confetti />

              {/* Círculo com troféu */}
              <div className="relative inline-flex mb-5">
                <div className="pulse-ring" />
                <motion.div
                  className="w-20 h-20 rounded-full flex items-center justify-center text-4xl"
                  style={{
                    background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})`,
                    boxShadow:  `0 8px 24px ${BRAND.gold}60`,
                  }}
                  animate={{ rotate: [0, 8, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                >
                  🏆
                </motion.div>
              </div>

              {/* Estrelas */}
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, rotate: -30 }}
                    animate={{ scale: 1, rotate: 0   }}
                    transition={{ delay: 0.1 + i * 0.08, type: "spring" }}
                  >
                    <Star size={20} fill={BRAND.gold} style={{ color: BRAND.gold }} />
                  </motion.div>
                ))}
              </div>

              <h2
                className="tut-heading text-2xl font-extrabold mb-1 leading-tight"
                style={{ color: BRAND.primaryDark }}
              >
                <span className="gold-shimmer">Mestre do Ilha Leads!</span>
              </h2>
              <p className="text-muted text-sm mb-7 leading-relaxed">
                Parabéns,{" "}
                <span className="font-semibold" style={{ color: BRAND.primary }}>
                  {user?.name?.split(" ")[0] || "Corretor"}
                </span>
                ! Você completou 100% do tutorial e está pronto para vender mais imóveis.
              </p>

              <div className="flex gap-3">
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => { setMostrarCelebracao(false); navigate("/dashboard"); }}
                  className="flex-1 py-3 rounded-xl font-bold text-white text-sm"
                  style={{
                    background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryLight})`,
                    boxShadow:  `0 4px 16px ${BRAND.primary}35`,
                  }}
                >
                  Ir para Dashboard
                </motion.button>
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setMostrarCelebracao(false)}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm border"
                  style={{
                    borderColor: `${BRAND.gold}40`,
                    color:       BRAND.goldDark,
                    background:  `${BRAND.gold}08`,
                  }}
                >
                  Fechar
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}